import {kmlWithFolders, gpx} from '@tmcw/togeojson'
import ExifReader from 'exifreader'
import JSZip from 'jszip'
import FitParser from 'fit-file-parser'
import {parseShp, parseDbf} from 'shpjs'
import {bbox} from '@turf/turf'

export const SHAPEFILE_EXTS = new Set(['shp', 'shx', 'dbf', 'prj', 'cpg', 'sbn', 'sbx', 'qix'])
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'avif', 'webp']
const VECTOR_EXTS = ['kml', 'kmz', 'geojson', 'json', 'gpx', 'fit', 'csv', 'zip']

export const IMPORT_EXTENSIONS = new Set([...VECTOR_EXTS, ...SHAPEFILE_EXTS, ...IMAGE_EXTS])
export const IMPORT_ACCEPT = [...VECTOR_EXTS, 'shp', 'shx', 'dbf', 'prj', 'cpg', ...IMAGE_EXTS]
  .map(e => `.${e}`).join(',')

export const MAX_IMPORT_FILE_BYTES = 200 * 1024 * 1024

// Parser failures carry a message that is safe to show to the user.
export class ImportError extends Error {}

export function fileExtension(name) {
  const dotIdx = name.lastIndexOf('.')
  return dotIdx >= 0 ? name.slice(dotIdx + 1).toLowerCase() : ''
}

export function fileStem(name) {
  const dotIdx = name.lastIndexOf('.')
  return dotIdx >= 0 ? name.slice(0, dotIdx) : name
}

export async function readFile(file, asText = false) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => resolve(event.target.result)
    reader.onerror = (event) => reject(event.target.error)
    if (asText) return reader.readAsText(file)
    return reader.readAsArrayBuffer(file)
  })
}

// ---------------------------------------------------------------------------
// Feature normalization
// ---------------------------------------------------------------------------

// Styling/bookkeeping keys togeojson emits; consumed here, then dropped so they
// don't show up as grouping columns.
const STYLE_KEYS = [
  'fill', 'fill-opacity', 'stroke', 'stroke-opacity', 'stroke-width',
  'label-color', 'label-opacity', 'label-scale',
  'icon', 'icon-color', 'icon-opacity', 'icon-scale', 'icon-heading', 'icon-offset', 'icon-offset-units',
  'marker-color', 'marker-size', 'marker-symbol',
  'styleUrl', 'styleHash', 'styleMap', 'visibility',
  'timestamp', 'timespan', 'time', 'coordinateProperties',
  'desc', 'cmt', 'Description'
]

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

function strip2D(coords) {
  if (typeof coords[0] === 'number') return coords.length > 2 ? coords.slice(0, 2) : coords
  return coords.map(strip2D)
}

// The feature table only accepts these types (type_check constraint).
const PART_OF = { MultiPoint: 'Point', MultiLineString: 'LineString' }

function toStorableGeometry(geometry) {
  const part = PART_OF[geometry.type]
  if (!part) return geometry
  if (geometry.coordinates.length === 1) return { type: part, coordinates: geometry.coordinates[0] }
  return { type: 'GeometryCollection', geometries: geometry.coordinates.map(c => ({ type: part, coordinates: c })) }
}

function flattenGeometries(geometries) {
  return geometries.flatMap(g => g?.type === 'GeometryCollection' ? flattenGeometries(g.geometries || []) : [g])
    .filter(g => g && g.coordinates)
}

function normalizeGeometry(geometry) {
  if (!geometry) return null
  if (geometry.type === 'GeometryCollection') {
    const parts = flattenGeometries(geometry.geometries || []).map(normalizeGeometry).filter(Boolean)
      .flatMap(p => p.type === 'GeometryCollection' ? p.geometries : [p])
    if (!parts.length) return null
    if (parts.length === 1) return parts[0]
    // togeojson turns every KML MultiGeometry into a GeometryCollection; all-polygon
    // ones go back to MultiPolygon.
    if (parts.every(p => p.type === 'Polygon' || p.type === 'MultiPolygon')) {
      return {
        type: 'MultiPolygon',
        coordinates: parts.flatMap(p => p.type === 'MultiPolygon' ? p.coordinates : [p.coordinates])
      }
    }
    const members = parts.flatMap(p => p.type === 'MultiPolygon'
      ? p.coordinates.map(c => ({ type: 'Polygon', coordinates: c }))
      : [p])
    return { type: 'GeometryCollection', geometries: members }
  }
  if (!Array.isArray(geometry.coordinates) || !geometry.coordinates.length) return null
  return toStorableGeometry({ type: geometry.type, coordinates: strip2D(geometry.coordinates) })
}

const HTML_TAG = /<[a-z][\s\S]*>/i
const NAME_KEY = /^name$/i
// GIS bookkeeping columns that carry no meaning for users.
const NOISE_KEY = /^(fid|oid|objectid|shape|shape_.*)$/i
const NULL_VALUE = /^(<null>|null)$/i

function cellsOf(row) {
  return Array.from(row.childNodes).filter(n => /^t[dh]$/i.test(n.nodeName || ''))
}

// Key/value rows of an HTML description — ArcGIS popup tables put a feature's
// attributes there instead of in ExtendedData.
function htmlTableAttributes(doc) {
  const attributes = {}
  for (const row of Array.from(doc.getElementsByTagName('tr'))) {
    const cells = cellsOf(row)
    if (cells.length !== 2 || cells.some(c => c.getElementsByTagName('table').length)) continue
    const key = cells[0].textContent.trim()
    const value = cells[1].textContent.trim()
    if (!key || !value || NULL_VALUE.test(value) || NOISE_KEY.test(key) || Object.hasOwn(attributes, key)) continue
    attributes[key] = value
  }
  return attributes
}

function htmlToText(html) {
  const withBreaks = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li|tr|h\d)>/gi, '$&\n')
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html')
  return (doc.body?.textContent || doc.documentElement?.textContent || '')
    .split('\n').map(l => l.trim()).filter(Boolean).join('\n')
}

// togeojson wraps CDATA descriptions as {"@type": "html", value}. HTML is
// reduced to plain text: table rows become attributes plus a "Key: value" list.
function extractDescription(props) {
  const raw = props.description ?? props.Description ?? props.desc ?? props.cmt
  const text = typeof raw === 'object' && raw !== null ? raw.value : raw
  if (typeof text !== 'string' || !text.trim()) return { description: '', attributes: {} }
  if (!HTML_TAG.test(text)) return { description: text.trim(), attributes: {} }

  const attributes = htmlTableAttributes(new DOMParser().parseFromString(text, 'text/html'))
  if (!Object.keys(attributes).length) return { description: htmlToText(text), attributes }
  const description = Object.entries(attributes)
    .filter(([k]) => !NAME_KEY.test(k))
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n')
  return { description, attributes }
}

function extractColor(props, geomType) {
  const polygonFill = props['fill-opacity'] === 0 ? null : props.fill
  const candidates = geomType?.includes('Polygon')
    ? [props.color, polygonFill, props.stroke]
    : geomType?.includes('LineString')
      ? [props.color, props.stroke]
      : [props.color, props['marker-color'], props['icon-color']]
  return candidates.find(c => typeof c === 'string' && HEX_COLOR.test(c)) || undefined
}

function extractTime(props) {
  const raw = props.timestamp || props.timespan?.begin || props.time
  const t = raw ? Date.parse(raw) : NaN
  return Number.isFinite(t) ? t : undefined
}

function normalizeFeature(feature, fallbackName) {
  const geometry = normalizeGeometry(feature.geometry)
  const props = feature.properties || {}
  if (!geometry && !props.needsLocation) return null
  if (props['@geometry-type'] === 'groundoverlay') return null

  const out = { ...props }
  for (const k of STYLE_KEYS) delete out[k]
  delete out['@geometry-type']

  const { description, attributes } = extractDescription(props)
  for (const [k, v] of Object.entries(attributes)) {
    if (!Object.hasOwn(out, k)) out[k] = v
  }
  const tableName = Object.entries(attributes).find(([k]) => NAME_KEY.test(k))?.[1]
  out.name = String(props.name || props.Name || tableName || fallbackName)
  out.description = description
  const color = extractColor(props, geometry?.type)
  if (color) out.color = color
  else delete out.color
  const time = extractTime(props)
  if (time) out._time = time

  return { type: 'Feature', geometry, properties: out }
}

// Heuristic: bbox outside WGS84 bounds suggests a projected CRS.
// False-negatives possible for small-area grids near 0,0; the warning is advisory.
function looksProjected(collection) {
  if (!collection.features.some(f => f.geometry)) return false
  try {
    const [minX, minY, maxX, maxY] = bbox(collection)
    return minX < -180 || maxX > 180 || minY < -90 || maxY > 90
  } catch (e) {
    return false
  }
}

function buildLayer(name, rawFeatures, fallbackName = name) {
  const features = []
  let skipped = 0
  for (const f of rawFeatures) {
    const n = normalizeFeature(f, fallbackName)
    if (n) features.push(n)
    else skipped++
  }
  return { name, collection: { type: 'FeatureCollection', features }, skipped }
}

// ---------------------------------------------------------------------------
// XML (KML / KMZ / GPX)
// ---------------------------------------------------------------------------

function decodeXml(buffer) {
  const bytes = new Uint8Array(buffer)
  if (bytes[0] === 0xFF && bytes[1] === 0xFE) return new TextDecoder('utf-16le').decode(bytes)
  if (bytes[0] === 0xFE && bytes[1] === 0xFF) return new TextDecoder('utf-16be').decode(bytes)
  const head = new TextDecoder('latin1').decode(bytes.subarray(0, 256))
  const declared = /<\?xml[^>]*encoding=["']([\w.:-]+)["']/i.exec(head)?.[1]
  try {
    return new TextDecoder(declared || 'utf-8').decode(bytes)
  } catch (e) {
    return new TextDecoder('utf-8').decode(bytes)
  }
}

function parseXML(text, fileName) {
  const cleanData = text.replace(/xsi:schemaLocation=["'][^"']*["']\s*/g, '')
  const doc = new DOMParser().parseFromString(cleanData, 'application/xml')
  if (doc.getElementsByTagName('parsererror').length) {
    throw new ImportError(`${fileName} is not valid XML`)
  }
  return doc
}

// Flattens kmlWithFolders' tree, tagging each feature with its folder path so
// FileViewer can group by it.
function flattenKmlTree(node, path = [], out = []) {
  for (const child of node.children || []) {
    if (child.type === 'folder') {
      flattenKmlTree(child, [...path, child.meta?.name || 'Folder'], out)
    } else if (child.type === 'Feature') {
      if (path.length) child.properties = { ...child.properties, folder: path.join(' / ') }
      out.push(child)
    }
  }
  return out
}

function kmlToLayer(text, name, fileName) {
  const tree = kmlWithFolders(parseXML(text, fileName))
  return buildLayer(name, flattenKmlTree(tree))
}

export const parseKML = async (file) => {
  const text = decodeXml(await readFile(file))
  return { layers: [kmlToLayer(text, file.name, file.name)] }
}

export const parseKMZ = async (file) => {
  const zip = await new JSZip().loadAsync(file)
  const kmlPaths = Object.keys(zip.files)
    .filter(p => p.toLowerCase().endsWith('.kml') && !p.includes('__MACOSX'))
  if (!kmlPaths.length) throw new ImportError(`${file.name} contains no KML document`)

  // Per the KMZ spec the root doc.kml is the entry point; other KMLs are
  // usually NetworkLink targets. Without one, import every KML as its own layer.
  const doc = kmlPaths.find(p => p.toLowerCase() === 'doc.kml')
  const paths = doc ? [doc] : kmlPaths
  const layers = []
  for (const path of paths) {
    const text = decodeXml(await zip.files[path].async('arraybuffer'))
    const name = paths.length === 1 ? file.name : `${fileStem(file.name)} / ${path.split('/').pop()}`
    layers.push(kmlToLayer(text, name, file.name))
  }
  return { layers }
}

export const parseGPX = async (file) => {
  const text = decodeXml(await readFile(file))
  const fc = gpx(parseXML(text, file.name))
  return { layers: [buildLayer(file.name, fc.features)] }
}

// ---------------------------------------------------------------------------
// GeoJSON
// ---------------------------------------------------------------------------

function isWgs84Crs(crs) {
  const name = crs?.properties?.name
  return !name || /(4326|CRS84)$/i.test(name)
}

function jsonToLayers(parsed, name) {
  if (parsed?.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
    return [buildLayer(name, parsed.features)]
  }
  if (parsed?.type === 'Feature') return [buildLayer(name, [parsed])]
  if (parsed?.type && (parsed.coordinates || parsed.geometries)) {
    return [buildLayer(name, [{ type: 'Feature', properties: {}, geometry: parsed }])]
  }
  throw new ImportError(`${name} is not GeoJSON`)
}

function parseJsonText(text, name) {
  let parsed
  try {
    parsed = JSON.parse(text)
  } catch (e) {
    throw new ImportError(`${name} is not valid JSON`)
  }
  const warn = isWgs84Crs(parsed?.crs) ? null : 'projected'
  return { layers: jsonToLayers(parsed, name), warn }
}

export const parseGeoJSON = async (file) => parseJsonText(await readFile(file, true), file.name)

// ---------------------------------------------------------------------------
// CSV (lat/lon columns)
// ---------------------------------------------------------------------------

const LAT_COLUMNS = ['lat', 'latitude', 'y', 'lat_dd', 'decimallatitude']
const LON_COLUMNS = ['lon', 'lng', 'long', 'longitude', 'x', 'lon_dd', 'decimallongitude']

function splitCsvRows(text, delimiter) {
  const rows = []
  let row = []
  let field = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++ }
      else if (c === '"') quoted = false
      else field += c
    } else if (c === '"') {
      quoted = true
    } else if (c === delimiter) {
      row.push(field); field = ''
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      row.push(field); field = ''
      if (row.some(v => v !== '')) rows.push(row)
      row = []
    } else {
      field += c
    }
  }
  row.push(field)
  if (row.some(v => v !== '')) rows.push(row)
  return rows
}

export const parseCSV = async (file) => {
  const text = (await readFile(file, true)).replace(/^﻿/, '')
  const firstLine = text.slice(0, text.search(/\r?\n|$/))
  const delimiter = [',', ';', '\t'].reduce((best, d) =>
    firstLine.split(d).length > firstLine.split(best).length ? d : best, ',')
  const [header, ...rows] = splitCsvRows(text, delimiter)
  if (!header) throw new ImportError(`${file.name} is empty`)

  const lower = header.map(h => h.trim().toLowerCase())
  const latIdx = lower.findIndex(h => LAT_COLUMNS.includes(h))
  const lonIdx = lower.findIndex(h => LON_COLUMNS.includes(h))
  if (latIdx < 0 || lonIdx < 0) {
    throw new ImportError(`${file.name} needs latitude and longitude columns (e.g. "lat" and "lon")`)
  }

  const features = rows.map(r => {
    const lat = parseFloat(r[latIdx])
    const lon = parseFloat(r[lonIdx])
    const properties = Object.fromEntries(header.map((h, i) => [h.trim(), r[i] ?? '']))
    return {
      type: 'Feature',
      properties,
      geometry: Number.isFinite(lat) && Number.isFinite(lon) ? { type: 'Point', coordinates: [lon, lat] } : null
    }
  })
  return { layers: [buildLayer(file.name, features)] }
}

// ---------------------------------------------------------------------------
// Shapefiles
// ---------------------------------------------------------------------------

// DBF header byte 29 is the "language driver ID"; used when there is no .cpg.
const DBF_LDID_ENCODINGS = {
  0x01: 'ibm437', 0x02: 'ibm850', 0x03: 'windows-1252', 0x57: 'windows-1252', 0x58: 'windows-1252',
  0x59: 'windows-1252', 0x64: 'ibm852', 0x65: 'ibm866', 0x66: 'ibm865', 0x26: 'ibm866',
  0x4D: 'gbk', 0x4E: 'euc-kr', 0x4F: 'big5', 0x13: 'shift_jis', 0x7B: 'shift_jis', 0x7A: 'gbk',
  0x78: 'big5', 0x79: 'euc-kr', 0x7C: 'windows-874', 0x7D: 'windows-1255', 0x7E: 'windows-1256',
  0xC8: 'windows-1250', 0xC9: 'windows-1251', 0xCA: 'windows-1254', 0xCB: 'windows-1253', 0xCC: 'windows-1257'
}

function dbfEncoding(dbfBuffer, cpgText) {
  if (cpgText && cpgText.trim()) return cpgText.trim()
  return DBF_LDID_ENCODINGS[new Uint8Array(dbfBuffer)[29]]
}

function shapefileToLayer(name, {shpBuffer, dbfBuffer, prjText, cpgText}) {
  const geoms = parseShp(shpBuffer, prjText || undefined)
  const attrs = dbfBuffer ? parseDbf(dbfBuffer, dbfEncoding(dbfBuffer, cpgText)) : []
  const layer = buildLayer(name, geoms.map((g, i) => ({ type: 'Feature', geometry: g, properties: attrs[i] || {} })))
  // A .prj that proj4 couldn't read leaves coordinates projected; flag either way.
  const warn = looksProjected(layer.collection) ? (prjText ? 'bad-prj' : 'missing-prj') : null
  return { layer, warn }
}

// Manual walk so .geojson entries are picked up too — shpjs only recognises `.json`.
export const parseShapefileZip = async (file) => {
  const zip = await new JSZip().loadAsync(file)

  const bundles = new Map()
  const jsonEntries = []
  const kmlEntries = []
  for (const [path, entry] of Object.entries(zip.files)) {
    if (entry.dir) continue
    if (path.includes('__MACOSX') || path.split('/').pop().startsWith('.')) continue
    const ext = fileExtension(path)
    if (['geojson', 'json'].includes(ext)) jsonEntries.push({ path, entry })
    else if (ext === 'kml') kmlEntries.push({ path, entry })
    else if (SHAPEFILE_EXTS.has(ext)) {
      const stem = fileStem(path)
      if (!bundles.has(stem)) bundles.set(stem, {})
      bundles.get(stem)[ext] = entry
    }
  }

  const layers = []
  const warns = []
  for (const { path, entry } of jsonEntries) {
    const r = parseJsonText(await entry.async('string'), fileStem(path.split('/').pop()))
    layers.push(...r.layers)
    if (r.warn) warns.push(r.warn)
  }
  for (const { path, entry } of kmlEntries) {
    layers.push(kmlToLayer(decodeXml(await entry.async('arraybuffer')), fileStem(path.split('/').pop()), file.name))
  }
  for (const [stem, parts] of bundles.entries()) {
    if (!parts.shp) continue
    const { layer, warn } = shapefileToLayer(stem.split('/').pop(), {
      shpBuffer: await parts.shp.async('arraybuffer'),
      dbfBuffer: parts.dbf ? await parts.dbf.async('arraybuffer') : null,
      prjText: parts.prj ? await parts.prj.async('string') : null,
      cpgText: parts.cpg ? await parts.cpg.async('string') : null
    })
    layers.push(layer)
    if (warn) warns.push(warn)
  }

  if (!layers.length) throw new ImportError(`${file.name} contains no shapefile, GeoJSON or KML layers`)
  return { layers, warn: warns[0] || null }
}

export const parseShapefileBundle = async ({shp: shpFile, dbf, prj, cpg, fileName}) => {
  const { layer, warn } = shapefileToLayer(fileName, {
    shpBuffer: await readFile(shpFile),
    dbfBuffer: dbf ? await readFile(dbf) : null,
    prjText: prj ? await readFile(prj, true) : null,
    cpgText: cpg ? await readFile(cpg, true) : null
  })
  return { layers: [layer], warn }
}

// ---------------------------------------------------------------------------
// Images / FIT
// ---------------------------------------------------------------------------

function getImageGeometry(gps) {
  let latitude = gps.Latitude || gps.GPSLatitude.description
  let longitude = gps.Longitude || gps.GPSLongitude.description

  if (gps.GPSLatitudeRef && gps.GPSLatitudeRef.value[0] === 'S') {
    latitude = -latitude
  }

  if (gps.GPSLongitudeRef && gps.GPSLongitudeRef.value[0] === 'W') {
    longitude = -longitude
  }

  latitude = Math.max(-90, Math.min(90, latitude))
  longitude = Math.max(-180, Math.min(180, longitude))

  return { type: 'Point', coordinates: [longitude, latitude] }
}

function parseDate(s) {
  const b = s.split(/\D/)
  return new Date(b[0], b[1]-1, b[2], b[3], b[4], b[5])
}

export const parseImage = async (file) => {
  const tags = await ExifReader.load(file, { async: true })
  const created = tags.DateTimeOriginal ? parseDate(tags.DateTimeOriginal.value[0]).getTime() : file.lastModified
  const geometry = tags.GPSLatitude && tags.GPSLongitude
    ? getImageGeometry({
        GPSLatitude: tags.GPSLatitude,
        GPSLongitude: tags.GPSLongitude,
        GPSLatitudeRef: tags.GPSLatitudeRef,
        GPSLongitudeRef: tags.GPSLongitudeRef
      })
    : null

  const feature = {
    type: 'Feature',
    properties: {
      name: file.name,
      _time: created,
      resource: file,
      ...(geometry ? {} : { needsLocation: true })
    },
    geometry
  }
  return { layers: [buildLayer(file.name, [feature])] }
}

export const parseFIT = async (file) => {
  const buffer = await readFile(file)
  const parser = new FitParser({
    force: true,
    speedUnit: 'km/h',
    lengthUnit: 'km',
    temperatureUnit: 'celsius',
    elapsedRecordField: true,
    mode: 'list'
  })

  const data = await new Promise((resolve, reject) => {
    parser.parse(buffer, (error, result) => error ? reject(new ImportError(`${file.name} is not a valid FIT file`)) : resolve(result))
  })
  const coordinates = (data.records || [])
    .filter(record => record.position_lat !== undefined && record.position_long !== undefined)
    .map(record => [record.position_long, record.position_lat])
  const firstTime = data.records?.find(r => r.timestamp)?.timestamp
  const features = coordinates.length > 1
    ? [{
        type: 'Feature',
        properties: { name: file.name, ...(firstTime ? { time: new Date(firstTime).toISOString() } : {}) },
        geometry: { type: 'LineString', coordinates }
      }]
    : []
  return { layers: [buildLayer(file.name, features)] }
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

const PARSERS = {
  kml: parseKML,
  kmz: parseKMZ,
  geojson: parseGeoJSON,
  json: parseGeoJSON,
  gpx: parseGPX,
  fit: parseFIT,
  csv: parseCSV,
  zip: parseShapefileZip,
  ...Object.fromEntries(IMAGE_EXTS.map(e => [e, parseImage]))
}

function finalize(result) {
  const layers = result.layers.filter(l => l.collection.features.length)
  const skipped = result.layers.reduce((n, l) => n + (l.skipped || 0), 0)
  let warn = result.warn || null
  if (!warn && layers.some(l => looksProjected(l.collection))) warn = 'projected'
  return { layers, skipped, warn }
}

/**
 * Parses a single import file (or a loose shapefile bundle) into
 * `{ layers: [{name, collection}], skipped, warn }`.
 *
 * Throws ImportError with a user-facing message for unsupported, oversized,
 * malformed or empty inputs.
 */
export async function parseImportFile(fileOrBundle) {
  const isBundle = !(fileOrBundle instanceof Blob)
  const name = isBundle ? fileOrBundle.fileName : fileOrBundle.name

  if (isBundle) {
    if (!fileOrBundle.shp) throw new ImportError(`Missing .shp for ${name}`)
    if (!fileOrBundle.shx) throw new ImportError(`Missing .shx sidecar for ${name}`)
  }
  const blobs = isBundle ? Object.values(fileOrBundle).filter(v => v instanceof Blob) : [fileOrBundle]
  const oversized = blobs.find(b => b.size > MAX_IMPORT_FILE_BYTES)
  if (oversized) {
    throw new ImportError(`${oversized.name || name} is larger than ${MAX_IMPORT_FILE_BYTES / 1024 / 1024} MB`)
  }

  const parser = isBundle ? parseShapefileBundle : PARSERS[fileExtension(name)]
  if (!parser) throw new ImportError(`${name}: unsupported file type`)

  let result
  try {
    result = finalize(await parser(fileOrBundle))
  } catch (e) {
    if (e instanceof ImportError) throw e
    console.error(`Error parsing ${name}:`, e)
    throw new ImportError(`Could not read ${name}`)
  }
  if (!result.layers.length) throw new ImportError(`No features with a location found in ${name}`)
  return result
}

/** Splits dropped files into loose shapefile bundles (by stem) and everything else. */
export function partitionImportFiles(files) {
  const bundles = new Map()
  const others = []
  for (const file of files) {
    const ext = fileExtension(file.name)
    if (SHAPEFILE_EXTS.has(ext)) {
      const stem = fileStem(file.name)
      if (!bundles.has(stem)) bundles.set(stem, { fileName: `${stem}.shp` })
      bundles.get(stem)[ext] = file
    } else {
      others.push(file)
    }
  }
  return { bundles, others }
}
